import React, { useCallback, useEffect } from "react";
import {
  useAmILoggedInApplicant,
  useGetCareerPageJobs,
} from "../../../api-docs";
import useRouterVariable from "../../../../components/utils/useRouterVariable";
import { useCandidateOrgJobAppOverviewsQuery } from "../../../graphql.types";
import { useFilteredJobs } from "../utils/useFilteredJobs";
import { JobCardList } from "../components/job-card-list/job-card-list";
import { useJobCardListSort } from "../utils/useJobCardListSort";
import { ClientJobCardListItem } from "../types/client-job-card-list-item";
import { useRouter } from "next/router";
import { URLS } from "../../../../utils/urls";
import { openApplicantSignInModal } from "@/components/molecules/modals/ats/public-sign-in-modal.tsx/verify-email-sign-in-modal";
import { showToast } from "@/flokit/core/toast/show-toast";

export const ConnectedCandidateJobList = () => {
  const org = useRouterVariable("org", (o) => o as string);
  const candidateJobs = useGetCareerPageJobs(org as string, {
    query: {
      enabled: org != null,
    },
  });
  const jobsResponse = useFilteredJobs(candidateJobs);
  const login = useAmILoggedInApplicant();
  const candidateJobAppsResponse = useCandidateOrgJobAppOverviewsQuery(
    {
      orgFriendlyName: org,
    },
    {
      enabled: login.isSuccess && !!login?.data?.success && org != null,
    },
  );
  const router = useRouter();

  const applicantSignInAutoPrompt =
    router.query.applicantSignInAutoPrompt ?? "false";

  useEffect(() => {
    if (applicantSignInAutoPrompt === "true") {
      openApplicantSignInModal();
    }
  }, [applicantSignInAutoPrompt]);

  // When the user clicks on a job we want to take them to the job application.
  // The state where the extension is not availabe should never happen but
  // we'll show an error message if it does just in case.
  const onJobClick = useCallback(
    function openJobApplication(job: ClientJobCardListItem) {
      if (job.extension) {
        router.push(
          URLS.selfApplyJobApplication(
            router.query.org as string,
            job.extension,
          ),
        );
      } else {
        showToast({
          type: "error",
          message: "This job is not available for self-apply.",
        });
      }
    },
    [router],
  );

  const { sort, setSort } = useJobCardListSort();
  return (
    <JobCardList
      onJobClick={onJobClick}
      sort={sort}
      onSortChange={(sort) => setSort(sort, "replaceIn")}
      isLoading={jobsResponse.isLoading}
      jobs={jobsResponse.results}
      jobApps={candidateJobAppsResponse?.data?.publicCandidateOrgJobApps ?? []}
      isFiltered={jobsResponse.isFiltered}
    />
  );
};
